import { differenceInHours } from "date-fns";

import { QueryClient } from "@formant/core/client/QueryClient";
import { PromiseLruCache } from "@formant/core/common/PromiseLruCache";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { duration } from "@formant/core/model/duration";
import { IQuery } from "@formant/core/model/IQuery";
import { IStreamAggregateData } from "@formant/core/model/IStreamAggregateData";
import { IStreamData } from "@formant/core/model/IStreamData";

import { IAuthStore } from "./IAuthStore";
import { IOnDemandStore } from "./IOnDemandStore";
import { QueryAggregateSegmentCache } from "./QueryAggregateSegmentCache";
import { QuerySegmentCache } from "./QuerySegmentCache";

export class QueryCache {
    private minuteSegmentCache!: QuerySegmentCache;
    private hourSegmentCache!: QuerySegmentCache;
    private aggregateByHourCache!: QueryAggregateSegmentCache;
    private aggregateCache = new PromiseLruCache<
        string,
        IStreamAggregateData[]
    >({
        name: "QueryCache-aggregateCache",
        max: 250,
        ttl: 10 * duration.minute,
    });

    constructor(
        private queryClient: QueryClient,
        authStore: IAuthStore,
        onDemandStore: IOnDemandStore,
    ) {
        this.minuteSegmentCache = new QuerySegmentCache(
            "minute",
            queryClient,
            authStore,
            onDemandStore,
        );

        this.hourSegmentCache = new QuerySegmentCache(
            "hour",
            queryClient,
            authStore,
            onDemandStore,
        );

        this.aggregateByHourCache = new QueryAggregateSegmentCache(
            "hour",
            queryClient,
            authStore,
            onDemandStore,
        );
    }

    public query(token: string, query: IQuery): Promise<IStreamData[]> {
        if (
            differenceInHours(
                parseIsoDate(query.end),
                parseIsoDate(query.start),
            ) > 2
        ) {
            // Use hour segements for larger queries.
            return this.hourSegmentCache.query(token, query);
        } else {
            return this.minuteSegmentCache.query(token, query);
        }
    }

    public queryAggregate(
        token: string,
        query: IQuery,
    ): Promise<IStreamAggregateData[]> {
        const key = JSON.stringify(query);
        let aggregates = this.aggregateCache.get(key);
        if (!aggregates) {
            aggregates = this.queryClient.queryAggregates(token, query);
            this.aggregateCache.set(key, aggregates);
        }
        return aggregates;
    }

    public queryAggregateByHour(
        token: string,
        query: IQuery,
    ): Promise<IStreamAggregateData[]> {
        return this.aggregateByHourCache.query(token, query);
    }
}
